import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DashboardTaxesCardComponent } from '@luggagehero/dashboard/taxes/ui';
import { TaxesActions, TaxesSelectors, TaxesState } from '@luggagehero/dashboard-taxes-data-access';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { TaxItem } from '@luggagehero/shared/interfaces';
import { SharedUiSpinnerComponent } from '@luggagehero/shared/ui';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'lh-dashboard-feature-taxes',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgFor,
    FormsModule,
    DashboardTaxesCardComponent,
    TranslateModule,
    SharedUiSpinnerComponent,
    NgFor,
    NgIf,
  ],
  templateUrl: './dashboard-feature-taxes.component.html',
  styleUrls: ['./dashboard-feature-taxes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardFeatureTaxesComponent implements OnInit, OnDestroy {
  private storageLocationId!: string;
  private paramsSubscription?: Subscription;
  private readonly store = inject<Store<TaxesState>>(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  taxes$ = this.store.select(TaxesSelectors.selectAllTaxes);
  draft: TaxItem | null = null;
  isSavingDraft$ = this.store.select(TaxesSelectors.selectIsSavingDraft);
  isLoading$ = this.store.select(TaxesSelectors.selectIsLoading);
  isTaxFeatureEnabled = true;
  private actions$ = inject(Actions);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.isTaxFeatureEnabled = AppConfig.STORAGE_MANAGER_ENABLE_TAX_FEATURE;

    if (this.isTaxFeatureEnabled) {
      this.paramsSubscription = this.route.params.subscribe((params) => {
        this.storageLocationId = String(params['id']);
        this.store.dispatch(TaxesActions.getTaxes({ storageLocationId: this.storageLocationId }));
      });

      this.actions$.pipe(ofType(TaxesActions.addTaxSuccess), takeUntil(this.destroy$)).subscribe(() => {
        this.draft = null;
      });
    }
  }

  ngOnDestroy(): void {
    this.paramsSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSaving(id: string | undefined): Observable<boolean> {
    if (id == null) {
      return of(false);
    }
    return this.store.select(TaxesSelectors.isSaving(id));
  }

  isDeleting(id: string | undefined): Observable<boolean> {
    if (id == null) {
      return of(false);
    }
    return this.store.select(TaxesSelectors.isDeleting(id));
  }

  deleteTax(id?: string): void {
    if (!id) {
      return;
    }
    const shouldDelete = confirm(this.translate.instant('CONFIRM_DELETE_TAX_ITEM') as string);

    if (!shouldDelete) {
      return;
    }

    this.store.dispatch(
      TaxesActions.deleteTax({
        payload: {
          storageLocationId: this.storageLocationId,
          taxItemId: id,
        },
      }),
    );
  }

  saveTaxChanges(editModel: TaxItem): void {
    if (editModel._id != null) {
      this.store.dispatch(
        TaxesActions.updateTax({
          payload: {
            storageLocationId: this.storageLocationId,
            taxItem: editModel,
            taxItemId: editModel._id,
          },
        }),
      );
    } else {
      this.store.dispatch(
        TaxesActions.addTax({
          payload: {
            storageLocationId: this.storageLocationId,
            taxItem: editModel,
          },
        }),
      );
    }
  }

  createDraft(): void {
    this.draft = {
      name: '',
      rates: [{ rate: 0, validFrom: new Date() }],
      taxType: 'sales' as TaxItem['taxType'],
    };
  }

  discardDraft(): void {
    this.draft = null;
  }
}
